import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Tooltip } from 'antd';
import type React from 'react';
import { useEffect, useState } from 'react';

export interface VersionInfo {
  version: string;
  buildDate: string;
  gitCommit: string;
  gitBranch: string;
  isDirty?: boolean;
  isDevelopment?: boolean;
  baseVersion?: string;
}
export const loadVersionInfo = async (
  setVersionInfo: (data: VersionInfo) => void,
  setLoading: (loading: boolean) => void,
) => {
  try {
    const response = await fetch('/version.json');
    if (response.ok) {
      const data = await response.json();
      setVersionInfo(data);
    }
  } catch (error) {
    console.warn('Could not load version info:', error);
  } finally {
    setLoading(false);
  }
};

export const Version: React.FC = () => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVersionInfo(setVersionInfo, setLoading);
  }, []);

  if (loading || !versionInfo) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const shortCommit = versionInfo.gitCommit.substring(0, 7);

  // Determine tag color based on status
  const getTagColor = () => {
    if (versionInfo.isDevelopment) {
      return versionInfo.isDirty ? 'orange' : 'green';
    }
    return 'blue';
  };

  // Create version display text
  const getVersionText = () => {
    let text = `v${versionInfo.version}`;
    if (versionInfo.isDevelopment) {
      text += ' DEV';
    }
    if (versionInfo.isDirty) {
      text += ' *';
    }
    return `${text} (${shortCommit})`;
  };

  return (
    <Tooltip
      title={
        <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
          <div>
            <strong>Version:</strong> {versionInfo.version}
          </div>
          {versionInfo.baseVersion && versionInfo.baseVersion !== versionInfo.version && (
            <div>
              <strong>Base Version:</strong> {versionInfo.baseVersion}
            </div>
          )}
          <div>
            <strong>Build Date:</strong> {formatDate(versionInfo.buildDate)}
          </div>
          <div>
            <strong>Git Commit:</strong> {versionInfo.gitCommit}
          </div>
          <div>
            <strong>Git Branch:</strong> {versionInfo.gitBranch}
          </div>
          {versionInfo.isDevelopment && (
            <div>
              <strong>Mode:</strong> Development
            </div>
          )}
          {versionInfo.isDirty && (
            <div style={{ color: '#ff7875' }}>
              <strong>Status:</strong> Uncommitted changes
            </div>
          )}
        </div>
      }
      placement="topLeft"
    >
      <Tag color={getTagColor()} icon={<InfoCircleOutlined />} style={{ cursor: 'pointer', fontSize: '11px' }}>
        {getVersionText()}
      </Tag>
    </Tooltip>
  );
};
